// File: apps/web/src/lib/api/types-collector-keys.ts
// Version: 2026.4.1 | Initial — Phase 2 Wave 2 collector-key UI types
//
// Mirrors ForgeTrack.Api.DTOs.CollectorKeyDtos. Keep the shapes in sync —
// these are sent over the wire on every request/response for the
// /api/v1/settings/collector-keys endpoints.

// ─── Request shapes ─────────────────────────────────────────────────────────

export interface CreateCollectorKeyRequest {
  name:          string;
  scopes:        string[];
  expiresInDays?: number | null;
}

export interface RevokeCollectorKeyRequest {
  reason?: string | null;
}

// ─── Response shapes ────────────────────────────────────────────────────────

/**
 * Returned once from POST /api/v1/settings/collector-keys. The `plainToken`
 * field is the ONLY time the full secret is exposed — subsequent list
 * calls only return the masked prefix.
 */
export interface MintedCollectorKey {
  id:         number;
  name:       string;
  plainToken: string;
  prefix:     string;
  scopes:     string[];
  expiresAt:  string | null;   // ISO-8601 UTC
  createdAt:  string;           // ISO-8601 UTC
}

export interface CollectorKeySummary {
  id:            number;
  name:          string;
  maskedPrefix:  string;        // "ftcc_abc…•••x9k2"
  scopesJson:    string;        // JSON-encoded string[] — parse before rendering
  createdAt:     string;
  expiresAt:     string | null;
  revokedAt:     string | null;
  revokedReason: string | null;
  lastUsedAt:    string | null;
  lastUsedIp:    string | null;
  usageCount:    number;
}

export interface SubscriptionLimitsResponse {
  tier:                     string;
  maxCollectorKeys:         number;
  maxCollectorKeysFromTier: number;
  hasOverride:              boolean;
  activeKeys:               number;
}

// ─── Canonical scope vocabulary ─────────────────────────────────────────────

/**
 * Supported scopes. Keep in sync with CollectorKeyService.IsSupportedScope
 * on the server. Adding a scope requires a code change on both sides.
 */
export const SUPPORTED_SCOPES = [
  {
    id:          'import:claude-code',
    label:       'Import Claude Code sessions',
    description: 'Upload JSONL session archives to /api/v1/import/claude-code.',
  },
] as const;

export type SupportedScope = (typeof SUPPORTED_SCOPES)[number]['id'];
